import AceEditor from "react-ace";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setCompleters } from "ace-builds/src-min-noconflict/ext-language_tools";
import { isPlainObject } from "lodash";
import ace from "ace-builds";
import "ace-builds/src-min-noconflict/mode-sql";
import "ace-builds/src-min-noconflict/mode-text";
import "ace-builds/src-min-noconflict/mode-html";
import "ace-builds/src-min-noconflict/mode-json";
import "ace-builds/src-min-noconflict/theme-twilight";
import "ace-builds/src-min-noconflict/mode-javascript";
import "ace-builds/src-min-noconflict/theme-chrome";
import classNames from "classnames";
import { crossIcon, expandIcon, compressIcon } from "../../../assets/icons";
import Alert, { selectAlert } from "../Alert";
import { actions as alertActions } from "../Alert/alert.reducers";
import {
  selectSelectedDataModelId,
  validateSqlExpression,
  selectCollections,
} from "../../../pages/DataModels";
import {
  ALERT_TIMEOUT,
  API_RESPONSES,
  EMPTY_STRING,
  MULESOFT,
  KEYDOWN,
  ESCAPE,
  DATA_TYPES,
  ACE_EDITOR_THEMES,
} from "../../../constants";
import Spinner, { SPINNER_NAMES } from "../Spinner";
import { useLocation } from "react-router-dom";
import { selectSelectedMulesoftEndpoint } from "../../../pages/Mulesoft";
import { Tooltip } from "../../molecules";
import ThemeToggler from "./AceEditorThemeToggler";
import { getFormattedCode } from "../../../helpers";

const { setAlert } = alertActions;
ace.config.set("basePath", "https://unpkg.com/ace-builds@1.4.6/src-noconflict");

const { SUCCESS } = API_RESPONSES;
const { VALIDATE_SQL_EXPRESSION } = SPINNER_NAMES;
const { JSON_MODE, TEXT, SQL, JAVASCRIPT, HTML } = DATA_TYPES;
const { DARK, LIGHT } = ACE_EDITOR_THEMES;
const DARK_THEME = "dark";
const LIGHT_THEME = "light";
const SERVICE_EDITOR_MODAL_THEME = "serviceEditorModalTheme";

const ServiceEditorModal = ({
  title,
  mode,
  name,
  values,
  shouldShow,
  setShouldShow,
  handleChange = () => {},
  setShouldShowExpression = () => {},
  handleOnClose = () => {},
  helperText = EMPTY_STRING,
}) => {
  const dispatch = useDispatch();
  const selectedAlert = useSelector(selectAlert);
  const { data: collections } = useSelector(selectCollections);
  const SelectedDataModelId = useSelector(selectSelectedDataModelId);
  const selectedMulesoftEndpoint = useSelector(selectSelectedMulesoftEndpoint);

  const isLightTheme =
    localStorage.getItem(SERVICE_EDITOR_MODAL_THEME) === LIGHT_THEME;

  const [inputData, setInputData] = useState();
  const [alertData, setAlertData] = useState();
  const [dataModelId, setDataModelId] = useState();
  const [collectionsRefNames, setCollectionsRefNames] = useState();
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
  const [isZoom, setIsZoom] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(!isLightTheme);
  const [errorMessage, setErrorMessage] = useState(EMPTY_STRING);
  const element = useRef();

  useEffect(() => {
    SelectedDataModelId && setDataModelId(SelectedDataModelId);
  }, [SelectedDataModelId]);

  useEffect(() => {
    selectedAlert && setAlertData(selectedAlert);
  }, [selectedAlert]);

  useEffect(() => {
    if (!collections) return;
    const collectionsRefNames = collections.map(({ refName }) => ({
      caption: refName,
      value: refName,
      meta: "collection",
    }));
    setCollectionsRefNames(collectionsRefNames);
  }, [collections]);

  useEffect(() => {
    values && setInputData(values[name]);
  }, [name, values]);

  useEffect(() => {
    if (mode === JSON_MODE) {
      try {
        const isJSONValid = isPlainObject(JSON.parse(inputData));
        setIsSubmitButtonDisabled(!isJSONValid);
      } catch (error) {
        setIsSubmitButtonDisabled(true);
      }
    }
  }, [inputData, mode]);

  useEffect(() => {
    if (inputData && (mode === TEXT || mode === HTML))
      setIsSubmitButtonDisabled(false);
  }, [inputData, mode]);

  useEffect(() => {
    /* eslint-disable no-new-func */
    if (mode === JAVASCRIPT) {
      try {
        new Function(inputData);
        setIsSubmitButtonDisabled(false);
      } catch (err) {
        setErrorMessage(err.message);
        setIsSubmitButtonDisabled(true);
      }
    }
    /* eslint-enable no-new-func */
  }, [inputData, mode]);

  const staticWordCompleter = useMemo(
    () => ({
      getCompletions: (editor, session, pos, prefix, callback) =>
        callback(null, collectionsRefNames),
    }),
    [collectionsRefNames]
  );

  useEffect(() => setCompleters([staticWordCompleter]), [staticWordCompleter]);

  const handleClose = () => {
    setShouldShow(false);
  };

  const handleOnSubmit = () => {
    try {
      const formattedCode = getFormattedCode(mode, inputData);
      handleChange(formattedCode);
    } catch (error) {
      console.error(error);
    }
    setShouldShow(false);
  };

  const handleThemeChange = () => {
    isDarkTheme
      ? element.current.classList.remove(DARK_THEME)
      : element.current.classList.add(DARK_THEME);
    const selectedTheme = !isDarkTheme ? DARK_THEME : LIGHT_THEME;
    localStorage.setItem(SERVICE_EDITOR_MODAL_THEME, selectedTheme);
    setIsDarkTheme(!isDarkTheme);
  };

  const handleOnExpression = () => {
    handleChange(inputData);
    setShouldShowExpression(true);
  };

  const location = useLocation();

  const validateSqlQuery = () => {
    const data = {
      expression: `'${inputData}'`,
      ...(location.pathname.includes(MULESOFT)
        ? { endpointId: selectedMulesoftEndpoint.id }
        : { dataModelId }),
    };
    dispatch(validateSqlExpression(data)).then(({ payload }) => {
      if (payload) {
        setIsSubmitButtonDisabled(false);
        dispatch(
          setAlert({
            type: SUCCESS,
            message: payload.data,
          })
        );
        setTimeout(() => dispatch(setAlert()), ALERT_TIMEOUT);
      }
    });
  };

  const handleOnEditorChange = (enteredQuery) => {
    setInputData(enteredQuery);
    mode === SQL && setIsSubmitButtonDisabled(true);
  };

  useEffect(() => {
    const close = ({ code }) => code === ESCAPE && setShouldShow(false);
    document.addEventListener(KEYDOWN, close);
    return () => document.removeEventListener(KEYDOWN, close);
  }, [setShouldShow]);

  return (
    <>
      <div
        className={classNames(
          "fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[999] overflow-auto transition-opacity duration-300 flex items-center ",
          {
            "px-4": !isZoom,
            " inline-block opacity-100": shouldShow,
            " w-0 h-0 opacity-0": !shouldShow,
          }
        )}
      >
        <div
          className={classNames(
            "bg-gray-800 border border-gray-700 rounded flex flex-col w-full h-full",
            {
              "rounded  mx-auto my-6 sm:w-[800px] sm:h-[500px]": !isZoom,
            }
          )}
        >
          <div>
            <div className="flex relative p-6 border-b border-gray-700">
              <div>
                <h6 className="left-0 text-base text-gray-50 font-bold">
                  {title}
                </h6>
              </div>
              <div className="absolute right-6 flex">
                <span ref={element}>
                  <ThemeToggler
                    isDarkTheme={isDarkTheme}
                    handleThemeChange={handleThemeChange}
                  />
                </span>
                <button
                  type="button"
                  className="ml-auto rounded-md bg-gray-900 mx-3"
                  onClick={() => setIsZoom(!isZoom)}
                >
                  <img
                    src={!isZoom ? expandIcon : compressIcon}
                    alt="zoom"
                    className="w-6"
                  />
                </button>
                <button
                  type="button"
                  className="ml-auto rounded-md bg-gray-900 mx-3"
                  onClick={() => {
                    setShouldShow(false);
                    handleOnClose();
                  }}
                >
                  <img src={crossIcon} alt="cross" className="w-6" />
                </button>
              </div>
            </div>
            {alertData && (
              <div className="m-2">
                <Alert
                  type={alertData.type}
                  message={alertData.message}
                  handleOnClose={setAlertData}
                />
              </div>
            )}
          </div>
          <div className="border-b-2 border-gray-700 text-white h-fit grow">
            <AceEditor
              mode={mode}
              theme={isDarkTheme ? DARK : LIGHT}
              name="aceEditor"
              height="100%"
              width="100%"
              value={inputData}
              onChange={handleOnEditorChange}
              setOptions={{
                showGutter: true,
                enableLiveAutocompletion: true,
                fontSize: 14,
                highlightActiveLine: true,
                tabSize: 4,
                useWorker: false,
                showPrintMargin: false,
              }}
            />
          </div>
          <div>
            <div className="px-6 py-3 border-t border-gray-700 flex align-center justify-end">
              {mode === SQL && (
                <button
                  className="btn btn-danger-light mr-1"
                  type="button"
                  onClick={validateSqlQuery}
                  disabled={isSpinnerActive || !inputData}
                >
                  <Spinner
                    name={VALIDATE_SQL_EXPRESSION}
                    setIsSpinnerActive={setIsSpinnerActive}
                    persistSize
                  >
                    Validate
                  </Spinner>
                </button>
              )}
              <button
                className="btn btn-primary-light mr-1"
                type="button"
                onClick={handleOnExpression}
              >
                Expression
              </button>
              <button
                className="btn btn-primary-light mr-1"
                type="button"
                onClick={handleClose}
              >
                Cancel
              </button>
              <Tooltip
                message={"Please validate your SQL query first"}
                shouldShow={mode === SQL && isSubmitButtonDisabled}
              >
                <button
                  className="btn btn-primary"
                  type="submit"
                  onClick={handleOnSubmit}
                  disabled={!inputData || isSubmitButtonDisabled}
                >
                  Submit
                </button>
              </Tooltip>
            </div>
            <div className="px-6 pb-2 flex flex-col align-center justify-start text-orange-600">
              {mode === JSON_MODE && inputData && isSubmitButtonDisabled && (
                <div className="text-red-500 p-6">Invalid JSON document</div>
              )}
              {mode === JAVASCRIPT && inputData && isSubmitButtonDisabled && (
                <div className="text-red-600 flex">
                  <p className="mr-2 font-bold">Error: </p>
                  <p className="whitespace-pre-line">{errorMessage}</p>
                </div>
              )}
              {helperText && (
                <div className="flex text-xs text-slate-200">
                  <p className="mr-2 font-bold">Note: </p>
                  <p className="whitespace-pre-line">{helperText}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceEditorModal;
